import { createSlice } from '@reduxjs/toolkit';
import { getTotalEngagement,
    getProviderPage,
    getTotalEngagementDigitalEngagementChart } from '../thunks/engagementThunks';
import { calculateYmax, createDistrictUSAMapData, 
    generateDistinctHexColors, 
    getOverallPhysicians, 
    handlePeriodChange, 
    makeBarDataSeries, 
    makeLabels, 
    removeEmptyNestedKeys,
    createStateUSAMapData,
    setTickAmount, 
    processDataForClickEngagement, 
    transformHospitalDataForBarChart,
    transformHospitalDataForMapChart } from '../../utility';
import { colors } from '../../constants';

const totalEngagementInitialState = {
    isLoading: true,
    error: null,
    engagementTotal: 0,
    isPeriodChangeIncrease: false,
    periodChange: 0,
    overallPhysicians: 0,
    stateOverallPhysicians: 0,
    overallTerritoriesPhysicians: 0,
    providers: [],
    filteredProviders: [],
    searchTerm: '',
    serverPaginated: false,
    isProvidersLoading: false,
    distributionRegion: [],
    stateDistributionRegion: [],
    distributionTerritories: [],
    districtBreakdownUSAMapData: [],
    stateBreakdownUSAMapData: [],
    territoriesBreakdownUSAMapData: [],
    channelEngagement: {
        labels: [],
        series: [],
        colors: []
    },
    digitalEngagement: {
        isLoading: true,
        error: null,
        labels: [],
        series: [],
        colors: []
    },
    segmentEngagement: {
        labels: [],
        series: [],
        colors: []
    },
    combinedSegment: [],
    clickEngagement: {
        labels: [],
        series: [],
        categoriesForAll: [],
        categories: [],
        actualData: {},
        selectedChannel: 'All',
        colors: [],
        yMax: 10,
        tickAmount: 5
    },
    currentPage: 1,
    pageSize: 20,
    totalRecordsFetch: 0,
    totalPages: 0,
    stateMapSeries: [],
    hospitalEngagementData: [],
    hospitalMapChart: [],
    hospitalBarChart: [],
    hospitalStateBarChart: [],
    overallHospitalPhysicians: 0,
    NpiEngagedChart: {
        labels: [],
        series: [],
        colors: []
    },
    stateDistributionRegionPercentage: [],
    distributionRegionPercentage: []
};

const totalEngagement = createSlice({
    name: 'totalEngagement',
    initialState: totalEngagementInitialState,
    reducers: {
        resetToInitialState(state, action) {
            Object.assign(state, totalEngagementInitialState);
        },
        setPhysicianEngagementChannel: (state, action) => {
            state.clickEngagement.selectedChannel = action.payload;
        },
        setPhysicianEngagementData: (state, action) => {
            const processedData = processDataForClickEngagement(action.payload, state.clickEngagement.actualData || {});
            state.clickEngagement.series = processedData;
            state.clickEngagement.yMax = calculateYmax(processedData,state.clickEngagement.selectedChannel);
        },
        setCurrentPage: (state, action) => {
            state.currentPage = action.payload;
        },
        handleSearch: (state, action) => {
            // Server-side search now -- see digitalEngagementReducer. The provider
            // list is one page, so filtering it here would only search the rows
            // already on screen. Record the term, reset to page 1, refetch.
            const { searchTerm } = action.payload;
            state.searchTerm = searchTerm ?? '';
            state.currentPage = 1;
        },
    },
    extraReducers: (builder) => {
        builder
            // Page turns and search go to the light provider-page endpoint
            // instead of re-running the whole total dashboard, exactly as the
            // digital tab does -- see digitalEngagementReducer.
            .addCase(getProviderPage.pending, (state) => {
                state.isProvidersLoading = true;
            })
            .addCase(getProviderPage.fulfilled, (state, action) => {
                state.isProvidersLoading = false;
                const rows = action.payload.engagement?.fmt_provider ?? [];
                const total = action.payload.engagement?.providers_total ?? rows.length;
                state.providers = rows;
                state.filteredProviders = rows;
                state.serverPaginated = true;
                state.totalRecordsFetch = total;
                state.totalPages = Math.ceil(total / (state.pageSize || 1));
            })
            .addCase(getProviderPage.rejected, (state) => {
                state.isProvidersLoading = false;
            })
            .addCase(getTotalEngagement.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = null;
                if (action.payload.engagement) {
                    state.engagementTotal = action.payload.engagement?.periodEngagement?.current;
                    {
                        const rows = action.payload.engagement?.fmt_provider ?? [];
                        // See digitalEngagementReducer: providers_total counts ALL
                        // matching providers, not just the page on screen.
                        const total = action.payload.engagement?.providers_total;
                        state.providers = rows;
                        state.filteredProviders = rows;
                        state.serverPaginated =
                            total !== undefined && total !== null && total > rows.length;
                        state.totalRecordsFetch = total ?? rows.length;
                        state.totalPages = Math.ceil(
                            (total ?? rows.length) / (state.pageSize || 1),
                        );
                    }
                    state.overallPhysicians = getOverallPhysicians(action.payload.engagement?.regionalEngagement || []);
                    state.stateOverallPhysicians = getOverallPhysicians(action.payload.engagement?.stateEngagement || []);

                    /* Channel Engagement */
                    const { labels, series } = processData(action.payload.engagement ? action.payload.engagement.channelEngagement : [], 'total');
                    state.channelEngagement.series = series;
                    state.channelEngagement.labels = labels.map((label => label.charAt(0).toUpperCase() + label.slice(1).toLowerCase()));

                    /* Segment Engagement */
                    state.segmentEngagement = processData(action.payload.engagement.segmentEngagement ?? null, null, true);
                    state.segmentEngagement.colors = colors;
                    const combinedData = action?.payload?.engagement?.combined_segment || {};
                    state.combinedSegment = makephysicianEngagementSegmentsDonutChartData(action.payload.engagement?.segmentEngagement || {},combinedData, true);

                    /* Period Change */
                    const { period, isPeriodChangeIncrease } = handlePeriodChange(action.payload.engagement?.periodEngagement?.current, action.payload.engagement?.periodEngagement?.previous);
                    state.periodChange = period;
                    state.isPeriodChangeIncrease = isPeriodChangeIncrease;

                    /* Breakdown Region */
                    state.distributionRegion = makeBarDataSeries(action.payload.engagement?.regionalEngagement) || [];
                    state.stateDistributionRegion = makeBarDataSeries(action.payload?.engagement?.stateEngagement,false,true) || [];

                    state.stateDistributionRegionPercentage = makeBarDataSeries(action.payload?.engagement?.stateEngagementPercentage,false,true,
                        false,false,false, true) || [];
                    state.distributionRegionPercentage = makeBarDataSeries(action.payload?.engagement?.districtEngagementPercentage || [], 
                        false,false,false,false,false, true) || [];

                    state.districtBreakdownUSAMapData = createDistrictUSAMapData(action.payload.engagement?.regionalEngagement) || [];
                    state.stateBreakdownUSAMapData = createDistrictUSAMapData(action.payload.engagement?.stateEngagement) || [];

                    state.stateMapSeries = createStateUSAMapData(action.payload?.engagement?.stateEngagement || []);

                    state.overallPhysicians = getOverallPhysicians(action.payload.engagement?.regionalEngagement || []);
                    // territories map
                    state.overallTerritoriesPhysicians = getOverallPhysicians(action.payload.engagement?.territoryEngagement || []);
                    state.distributionTerritories = makeBarDataSeries(action.payload?.engagement?.territoryEngagement || []) || [];
                    state.territoriesBreakdownUSAMapData = createDistrictUSAMapData(action.payload?.engagement?.territoryEngagement || [] ) ;
                    /* Click Engagement */
                    const newData = removeEmptyNestedKeys(action.payload.engagement.clickEngagement || {});
                    state.clickEngagement.actualData = newData;
                    state.clickEngagement.series = processDataForClickEngagement(state.clickEngagement.selectedChannel, newData || {});
                    state.clickEngagement.yMax = calculateYmax(state.clickEngagement.series, state.clickEngagement.selectedChannel);
                    state.clickEngagement.labels = makeLabels(newData, true, true, false, true);
                    state.clickEngagement.categoriesForAll = makeLabels(newData, true, false);
                    state.clickEngagement.categories = makeLabels(newData);
                    state.clickEngagement.colors = colors;

                    // hospital chart starts
                    const hospitalData = action.payload?.engagement?.hospitalEngagement || [];
                    state.hospitalEngagementData = hospitalData || [];
                    state.hospitalBarChart = transformHospitalDataForBarChart(hospitalData,false,true,'all') || [];
                    state.hospitalMapChart = transformHospitalDataForMapChart(hospitalData, 'all');
                    state.hospitalStateBarChart = transformHospitalDataForBarChart(hospitalData,true,false,'all') || [];
                    state.overallHospitalPhysicians = getOverallPhysicians(hospitalData || []);
                    // ends
                    //No. of Engagements
                    let data = action.payload?.engagement?.engagedEngagementDistribution || [];
                    data = data.filter(item=>item.count > 0);
                    state.NpiEngagedChart.labels = data.map(item=>item.label);
                    state.NpiEngagedChart.series = data.map(item=>item.count);
                    state.NpiEngagedChart.colors = colors;
                }   
            })
            .addCase(getTotalEngagement.pending, (state, action) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getTotalEngagement.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(getTotalEngagementDigitalEngagementChart.fulfilled, (state, action) => {
                state.digitalEngagement.isLoading = false;
                state.digitalEngagement.error = null;
                
                const triggerEngagement = action.payload?.triggerEngagement;
                
                if (triggerEngagement && Object.keys(triggerEngagement).length) {
                    const total = triggerEngagement.total_npi_id;
                    let calculatedData = {};
                    if (total > 0) {
                        // Calculate and round initial percentages
                        calculatedData = {
                            'FMT Match': Math.round((triggerEngagement.matched / total) * 100),
                            Recommend: Math.round((triggerEngagement.recommended / total) * 100),
                            None: Math.round((triggerEngagement.none / total) * 100)
                        };

                        // Calculate the sum of the rounded percentages
                        const sum = calculatedData['FMT Match'] + calculatedData.Recommend + calculatedData.None;

                        // Adjust the largest percentage if the sum is not 100
                        if (sum !== 100) {
                            const maxKey = Object.keys(calculatedData).reduce((a, b) => calculatedData[a] > calculatedData[b] ? a : b);
                            calculatedData[maxKey] += 100 - sum;
                        }
                    } else{
                        calculatedData = {
                            'FMT Match': 0,
                            Recommend: 0,
                            None: 100
                        };
                    }
                    state.digitalEngagement = processData(calculatedData, null, true);
                    state.digitalEngagement.colors = colors;
                }
                
            })
            .addCase(getTotalEngagementDigitalEngagementChart.pending, (state, action) => {
                state.digitalEngagement.isLoading = true;
                state.digitalEngagement.error = null;
            })
            .addCase(getTotalEngagementDigitalEngagementChart.rejected, (state, action) => {
                state.digitalEngagement.isLoading = false;
                state.digitalEngagement.error = action.payload;
            });
    }
});

const makeSeries = (data, key, isSort=false) => {
    const series = [];
    let totalSum = 0;

    if (!key) {
        if(isSort){
            const sortedKeys = Object.keys(data).sort();
            return sortedKeys.map((key => Number(data[key]) ));
        } 

        for (const category in data) {
            if (data[category]) {
                series.push(Number(data[category]));
            }

        }
        return series;
    }

    for (const category in data) {
        totalSum += data[category][key] || 0;
    }
    if (totalSum !== 0) {
        for (const category in data) {
            const value = data[category][key] || 0;
            const percentage = (value !== 0 && totalSum !== 0 ? ((value / totalSum) * 100) : 0);
            series.push(Number(percentage.toFixed(2)));
        }
    }

    return series;
};

const processData = (data, key, isSort = false) => {
    let labels = [], series = [];
    if(isSort){
        labels = makeLabels(data, false, false, isSort);
        series = makeSeries(data, key, isSort);
    } else{
        labels = makeLabels(data);
        series = makeSeries(data, key);
    }
    return { labels, series };
};


const makephysicianEngagementSegmentsDonutChartData = (a, b, isSort = false) => {
    const fallbackB = b || {}; // fallback if b is null/undefined

    const result = Object.entries(a).map(([name, value]) => {
        // Case-insensitive match key from b
        const lookupKey = Object.keys(fallbackB).find(
            bKey => bKey.toLowerCase() === name.toLowerCase()
        );

        const engagementData = lookupKey ? fallbackB[lookupKey] : {};

        return {
            name,
            y: parseFloat(value),
            ...(engagementData?.engaged !== undefined && { engaged: engagementData.engaged }),
            ...(engagementData?.non_engaged !== undefined && { non_engaged: engagementData.non_engaged })
        };
    });

    // Sort by y descending if isSort is true
    return isSort
        ? result.sort((a, b) => a.name.localeCompare(b.name))
        : result;
};

export const { actions } = totalEngagement;
export default totalEngagement.reducer;